import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Flower, ArrowRight, Heart } from "@phosphor-icons/react";
import { API } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";

export default function Invite() {
  usePageMeta({ title: "You've been invited", description: "Someone thinks a Wingwomen peer understands your reproductive-health path, open your anonymous intro card." });
  const { code } = useParams();
  const [share, setShare] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shares/${code}`).then(r => setShare(r.data)).catch(() => setErr("This invite link is no longer valid."));
  }, [code]);

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Flower size={26} weight="duotone" className="text-coral-700" />
          <span className="font-display text-2xl">Wingwomen</span>
        </Link>
        <Link to="/auth?mode=register" data-testid="invite-cta-top"
              className="text-sm bg-ink text-cream px-4 py-2 rounded-full hover:bg-coral transition-colors">
          Join the circle
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        {err ? (
          <div className="text-center py-24 text-muted-foreground" data-testid="invite-error">{err}</div>
        ) : !share ? (
          <div className="text-muted-foreground">Opening your invite…</div>
        ) : (
          <>
            <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />You've been invited</div>
            <h1 className="font-display text-5xl leading-tight">
              Someone thinks<br />
              <span className="italic text-coral-700">{share.match_anon}</span> understands your path.
            </h1>

            <div className="mt-10 bg-white border border-border rounded-2xl p-8 shadow-lift" data-testid="invite-card">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-coral-50 border border-coral-100 grid place-items-center font-display text-lg text-coral-700">
                  {share.match_anon.slice(0, 2)}
                </div>
                <div>
                  <div className="font-display text-2xl">{share.match_anon}</div>
                  <div className="text-sm text-muted-foreground">{share.stage} · {share.score}% shared journey</div>
                </div>
              </div>

              <div className="mt-6 space-y-2">
                {["conditions","treatments","goals"].map(k => (
                  share.shared?.[k]?.length > 0 && (
                    <div key={k} className="flex flex-wrap gap-1.5">
                      <span className="text-[10px] tracking-[0.15em] uppercase text-sage-600 mr-1 mt-1.5">{k}</span>
                      {share.shared[k].map(x => (
                        <span key={x} className="text-xs px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{x}</span>
                      ))}
                    </div>
                  )
                ))}
              </div>

              {share.note && (
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Heart size={12} weight="duotone" className="text-coral-700" /> A note from {share.from_anon}
                  </div>
                  <p className="text-ink/80 leading-relaxed italic">"{share.note}"</p>
                </div>
              )}
            </div>

            <Link to="/auth?mode=register" data-testid="invite-cta-main"
                  className="mt-8 inline-flex items-center gap-2 bg-coral text-white px-6 py-3.5 rounded-full font-medium shadow-lift hover:bg-coral-600 transition-colors">
              Join Wingwomen to connect <ArrowRight size={18} />
            </Link>
            <p className="text-xs text-muted-foreground mt-6">
              Free to start · Anonymous by default · Not medical advice
            </p>
          </>
        )}
      </main>
    </div>
  );
}
